import React, { useEffect, useContext, useState } from 'react'

import IBillingInfo from './billing-info.interface'
import Spinner from './../spinner/spinner'
import DashboardContext from '../layout/dashboard-context'
import MessageContext from '../message/message-context'
import InputFormikField from '../forms/input-formik-field'
import { Formik, Form, FormikValues } from 'formik'
import * as Yup from 'yup'
import { useSearchParams } from 'react-router-dom'
import api from '../../api'

interface BillingInfoProps {
  fromForm: boolean
}

const BillingInfo: React.FC<IBillingInfo> = ({ fromForm }: BillingInfoProps) => {
  // const { receivedData, setReceivedData } = useContext(BillingInfoContext)
  const { selectedOrganization } = useContext(DashboardContext)
  const { setMessage, setErrorMessage } = useContext(MessageContext)
  const [searchParams, setSearchParams] = useSearchParams()

  const [loading, setLoading] = useState(false)
  const [hasReceivedData, setHasReceivedData] = useState(false)

  const [billingData, setBillingData] = useState<Record<string, string>>({
    billingAdressPrimary: '',
    billingAdressSecondary: '',
    glnCode: '',
    zipCode: '',
    city: '',
    contactPerson: '',
    billingEmail: '',
  })

  const postNewData = async (values: FormikValues) => {
    setLoading(true)
    let res
    try {
      res = await updateBillingInfo(values)
    } catch (e) {
      setErrorMessage('Något gick fel med uppladdningen')
    }
    setLoading(false)
    if (res && res.data) {
      setMessage({ value: 'Informationen är uppdaterad' })

      getData()

      if (fromForm) {
        searchParams.delete('step')
        setSearchParams({ step: '3' })
      }
    } else {
      setErrorMessage('Något gick fel med uppladdningen')
    }
  }

  const updateBillingInfo = async (values: FormikValues) => {
    try {
      const newData = values
      const res = await api.post(`/accounts/${selectedOrganization.id}/billing`, newData)

      return res.data
    } catch (e) {
      return []
    }
  }

  const getBillingInfo = async () => {
    try {
      setHasReceivedData(false)
      const res = await api.get(`/accounts/${selectedOrganization.id}/billing`)

      setHasReceivedData(true)

      return res.data.data
    } catch (e) {
      return []
    }
  }

  const getData = async () => {
    if (selectedOrganization && selectedOrganization.id) {
      const billingData = await getBillingInfo()
      setBillingData(billingData)
    }
  }

  useEffect(() => {
    getData()
  }, [selectedOrganization])

  return (
    <div className={`flex flex-col w-full bg-white gap-8 ${fromForm ? '' : 'card  md:p-12 p-2"'}`}>
      {hasReceivedData ? (
          <div className="flex px-6 justify-between w-full flex-col">
            <h1>Faktureringsuppgifter</h1>
            {fromForm && (
              <span>
                Vänligen se över att nedanstående information fortfarande stämmer för företaget.
              </span>
            )}

            <Formik
              enableReinitialize
              initialValues={billingData}
              validationSchema={validationSchema}
              onSubmit={postNewData}
            >
              {({ errors, touched }) => {
                return (
                  <Form translate="" className="w-full">
                    <InputFormikField
                      name="billingAdressPrimary"
                      label="Adress Rad 1"
                      placeholder="Primär Adress"
                      type="text"
                      errors={errors}
                      touched={touched}
                    />
                    <InputFormikField
                      name="billingAdressSecondary"
                      label="Adress Rad 2"
                      placeholder="Sekundär Adress"
                      type="text"
                      errors={errors}
                      touched={touched}
                    />
                    <InputFormikField
                      name="zipCode"
                      label="Postnummer"
                      placeholder="Postnummer"
                      type="text"
                      errors={errors}
                      touched={touched}
                    />
                    <InputFormikField
                      name="city"
                      label="Stad"
                      placeholder="Stad"
                      type="text"
                      errors={errors}
                      touched={touched}
                    />
                    <h2 className="border-top">Kontaktperson</h2>
                    <InputFormikField
                      name="contactPerson"
                      label="Namn"
                      placeholder="Namn"
                      type="text"
                      errors={errors}
                      touched={touched}
                    />
                    <InputFormikField
                      name="billingEmail"
                      label="Epost till fakturamottagare"
                      placeholder="Fakturamail"
                      type="text"
                      errors={errors}
                      touched={touched}
                    />
                    <InputFormikField
                      name="glnCode"
                      label="Digitaladress GLN / Peppol"
                      placeholder="Digitaladress GLN / Peppol"
                      type="text"
                      errors={errors}
                      touched={touched}
                    />
                    <div className="justify-start flex flex-row py-2 mt-8">
                      {loading ? (
                        <Spinner />
                      ) : (
                        <button className="btn btn-lg btn-long" type="submit">
                          {fromForm ? 'Uppdatera faktureringsuppgifter' : 'Spara'}
                        </button>
                      )}
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
      ) : (
        <div className="m-40">
          <Spinner />
        </div>
      )}
    </div>
  )
}

export default BillingInfo

const validationSchema = Yup.object().shape({
  billingAdressPrimary: Yup.string().required('Fältet är obligatoriskt'),
  billingAdressSecondary: Yup.string(),
  glnCode: Yup.string(),
  zipCode: Yup.string().required('Fältet är obligatoriskt'),
  city: Yup.string().required('Fältet är obligatoriskt'),
  contactPerson: Yup.string().required('Fältet är obligatoriskt'),
  billingEmail: Yup.string().email().required('Fältet är obligatoriskt'),
})
